import { Link } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ContactRoundIcon, SearchIcon } from 'lucide-react';
import {
  Avatar,
  EmptyState,
  ErrorNotice,
  PageHeading,
  Pagination,
  Panel,
  Pill,
  SkeletonTable,
} from '@bcecloud/portal-ui';
import { errorMessage, getList } from '@/lib/api';
import { useUrlPage, useUrlSearch } from '@/lib/use-list-query-state';
import { type ContactRecord } from '@/pages/contacts-shared';

/**
 * The client-contact directory — deliberately the SAME list grammar as
 * People → Directory (avatar rows, chevron, pagination): different records
 * (CRM contacts vs portal users), one UI. Internal staff only; the routes
 * 403 everyone else.
 */

export function ContactsPage(): React.ReactElement {
  return (
    <section className="animate-portal-rise">
      <PageHeading
        eyebrow="PEOPLE"
        title="Client contacts"
        subtitle="Everyone named on an event, folded into one directory — synced nightly from the events themselves."
      />
      <ContactsDirectory />
    </section>
  );
}

/** The contacts list body — embedded by the consolidated People directory. */
export function ContactsDirectory(): React.ReactElement {
  const { search, setSearch, debounced } = useUrlSearch();
  const { page, setPage } = useUrlPage();

  const contacts = useQuery({
    queryKey: ['contacts', debounced, page],
    queryFn: ({ signal }) =>
      getList<ContactRecord>(
        '/contacts',
        { page, ...(debounced === '' ? {} : { search: debounced }) },
        signal,
      ),
    placeholderData: keepPreviousData,
  });

  return (
    <div>
      <div className="relative mb-4 max-w-md">
        <SearchIcon
          aria-hidden
          className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-portal-muted"
        />
        <input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search name, email or client…"
          aria-label="Search contacts"
          className="w-full rounded-portal-input border border-portal-line bg-white py-2 pl-9 pr-3 text-portal-body outline-none focus:border-brand"
        />
      </div>

      {contacts.isError ? (
        <ErrorNotice title="Contacts unavailable" message={errorMessage(contacts.error)} />
      ) : contacts.isPending ? (
        <SkeletonTable rows={8} columns={['w-[40%]', 'w-[12%]']} label="Loading contacts…" />
      ) : contacts.data.items.length === 0 ? (
        <Panel>
          {debounced ? (
            <EmptyState message={`Nobody matches “${debounced}”.`} />
          ) : (
            <EmptyState
              icon={<ContactRoundIcon aria-hidden className="size-6" />}
              title="No contacts yet"
              message="The directory fills from event contacts on the nightly sync."
            />
          )}
        </Panel>
      ) : (
        <>
          <Panel className={contacts.isFetching ? 'opacity-60 transition-opacity' : undefined}>
            <ul>
              {contacts.data.items.map((contact) => (
                <li key={contact.id} className="border-b border-portal-line last:border-b-0">
                  <Link
                    to={`/contacts/${contact.id}`}
                    className="flex items-center gap-3 px-4 py-2.5 transition-colors hover:bg-portal-bg"
                  >
                    <Avatar name={contact.name || contact.email} size={32} />
                    <span className="min-w-0 flex-1">
                      <b className="block truncate text-portal-small">
                        {contact.name || contact.email}
                      </b>
                      <span className="block truncate text-portal-micro text-portal-muted">
                        {[contact.email, contact.accounts.join(', ')].filter(Boolean).join(' · ')}
                      </span>
                    </span>
                    <Pill>{contact.eventCount} event{contact.eventCount === 1 ? '' : 's'}</Pill>
                    <span aria-hidden="true" className="text-portal-muted">
                      ›
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </Panel>
          <Pagination
            page={contacts.data.pagination.page}
            totalPages={contacts.data.pagination.totalPages}
            onChange={setPage}
            summary={`${contacts.data.pagination.total} contact${contacts.data.pagination.total === 1 ? '' : 's'}`}
          />
        </>
      )}
    </div>
  );
}
